import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Input,
  Button,
  Space,
  Typography,
  Empty,
  Alert,
  message,
} from 'antd';
import {
  ThunderboltOutlined,
  SearchOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import type { PrebuiltPrompt, PrebuiltPromptType } from '@/api/types';
import { typeFromValue } from '@/api/prebuiltPrompts';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { StatusPill, type PillTone } from '@/components/StatusPill';

const { Text, Paragraph } = Typography;

interface PinPromptDrawerProps {
  open: boolean;
  onClose: () => void;
}

const TYPE_TONE: Record<PrebuiltPromptType, PillTone> = {
  QUICK_START: 'ink',
  AGENT_INSTRUCTION: 'violet',
};
const TYPE_LABEL: Record<PrebuiltPromptType, string> = {
  QUICK_START: 'Quick start',
  AGENT_INSTRUCTION: 'Agent instruction',
};

// Drawer for pinning one or more existing prebuilt prompts to the active
// agent. Lists merchant-scoped prompts, filters out anything already pinned,
// supports search and multi-select, and pins them in display order on save.

export const PinPromptDrawer = observer(function PinPromptDrawer({
  open,
  onClose,
}: PinPromptDrawerProps) {
  const {
    agentDetailStore: detail,
    prebuiltPromptStore: prompts,
    themeStore,
  } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && prompts.items.length === 0 && !prompts.isLoading) {
      void prompts.fetch();
    }
    if (open) {
      setSearch('');
      setSelected(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Candidates: prompts not already pinned to this agent + the current
  // agent's surface (Patient / Care team) align with the prompt's surface flags.
  const pinnedIds = useMemo(
    () => new Set(detail.pinnedPrompts.map((p) => p.prebuiltPromptId)),
    [detail.pinnedPrompts]
  );

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return prompts.items
      .filter((p) => !pinnedIds.has(p.id))
      .filter((p) => {
        if (!term) return true;
        const blob = `${p.description ?? ''} ${p.buttonText ?? ''} ${p.name ?? ''}`.toLowerCase();
        return blob.includes(term);
      })
      .sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''));
  }, [prompts.items, pinnedIds, search]);

  function toggle(id: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handlePin(): Promise<void> {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      const baseOrder = detail.pinnedPrompts.length;
      const ids = [...selected];
      // Pin sequentially to keep DisplayOrder predictable. (Could be Promise.all
      // if the order race doesn't matter — chose serial for determinism.)
      for (let i = 0; i < ids.length; i++) {
        await detail.pinPrompt(ids[i], baseOrder + i);
      }
      message.success(`Pinned ${ids.length} prompt${ids.length === 1 ? '' : 's'}`);
      trackEvent('Agent.PromptsPinned', { count: ids.length });
      setSelected(new Set());
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to pin prompts');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ThunderboltOutlined /> Pin prompts to this agent
        </span>
      }
      width={580}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {selected.size === 0
              ? 'Pick one or more prompts above'
              : `${selected.size} selected`}
          </Text>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={submitting}
              disabled={selected.size === 0}
              onClick={handlePin}
            >
              Pin {selected.size > 0 ? `${selected.size} ` : ''}
              prompt{selected.size === 1 ? '' : 's'}
            </Button>
          </Space>
        </div>
      }
    >
      <div style={{ padding: '18px 20px' }}>
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 14 }}>
          Pinned prompts surface as one-click chips above this agent's chat. Click a row to
          select it, then save to pin all selected prompts in order.
        </Paragraph>

        <Input
          allowClear
          placeholder="Search prompts by description or chip text…"
          prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />

        {prompts.isLoading && prompts.items.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Loading prompts…
          </Text>
        ) : prompts.error ? (
          <Alert type="error" showIcon message={prompts.error} />
        ) : candidates.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 13, color: isDark ? colors.slate[400] : colors.slate[500] }}>
                {search
                  ? `No prompts match "${search}".`
                  : pinnedIds.size > 0
                  ? 'Every existing prompt is already pinned to this agent.'
                  : 'No prebuilt prompts exist for this merchant. Author one in the Prebuilt prompts library first.'}
              </span>
            }
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              maxHeight: 480,
              overflow: 'auto',
              paddingRight: 4,
            }}
          >
            {candidates.map((p) => (
              <PromptRow
                key={p.id}
                prompt={p}
                selected={selected.has(p.id)}
                onToggle={() => toggle(p.id)}
                isDark={isDark}
              />
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
});

// ── Single prompt row ──────────────────────────────────────────────────────

const PromptRow = observer(function PromptRow({
  prompt,
  selected,
  onToggle,
  isDark,
}: {
  prompt: PrebuiltPrompt;
  selected: boolean;
  onToggle: () => void;
  isDark: boolean;
}) {
  const t = typeFromValue(prompt.type);
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 14px',
        textAlign: 'left',
        background: selected
          ? isDark ? `${colors.ink[700]}40` : colors.ink[50]
          : isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${selected ? colors.ink[600] : isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'border-color 120ms ease, background 120ms ease',
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.borderColor = colors.ink[500];
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.borderColor = isDark ? colors.slate[700] : colors.slate[200];
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: selected
            ? colors.ink[600]
            : isDark ? `${colors.ink[700]}55` : colors.ink[50],
          color: selected ? '#FFFFFF' : colors.ink[600],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          flexShrink: 0,
          transition: 'background 120ms ease',
        }}
      >
        {selected ? <CheckOutlined /> : <ThunderboltOutlined />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: isDark ? colors.slate[50] : colors.slate[900],
            marginBottom: 4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {prompt.description || (
            <span style={{ fontStyle: 'italic', opacity: 0.7 }}>(no description)</span>
          )}
        </div>
        {prompt.buttonText && (
          <div
            style={{
              fontSize: 11.5,
              fontFamily: "'JetBrains Mono', monospace",
              color: isDark ? colors.slate[400] : colors.slate[500],
              marginBottom: 6,
            }}
          >
            "{prompt.buttonText}"
          </div>
        )}
        <Space size={4} wrap>
          <StatusPill tone={TYPE_TONE[t]}>{TYPE_LABEL[t]}</StatusPill>
          {prompt.memberApp && <StatusPill tone="ink">Patient</StatusPill>}
          {prompt.merchantApp && <StatusPill tone="violet">Care team</StatusPill>}
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10.5,
              color: isDark ? colors.slate[500] : colors.slate[400],
            }}
          >
            #{prompt.id}
          </span>
        </Space>
      </div>
    </button>
  );
});

